import { Notify } from 'quasar';

// Thin wrappers around `Notify.create(...)` for the common
// `{ type: 'positive' | 'negative' | 'warning' | 'info', message, ... }` shape.
// Drops the type/field-ordering boilerplate that recurs across the codebase.
//
// `Opts` is derived from Quasar's own `Notify.create` parameter type minus
// `type` and `message` (which the helper sets). All other notify fields
// (`position`, `caption`, `timeout`, `color`, `icon`, `actions`, `html`,
// `avatar`, ...) pass through transparently.

type Opts = Omit<Parameters<typeof Notify.create>[0], 'type' | 'message'>;

export function notifyOk(message: string, opts?: Opts) {
  Notify.create({ type: 'positive', message, ...opts });
}

export function notifyErr(message: string, opts?: Opts) {
  Notify.create({ type: 'negative', message, ...opts });
}

export function notifyWarn(message: string, opts?: Opts) {
  Notify.create({ type: 'warning', message, ...opts });
}

export function notifyInfo(message: string, opts?: Opts) {
  Notify.create({ type: 'info', message, ...opts });
}

/**
 * The sentence to show a user for a failed API call.
 *
 * DRF answers in four shapes depending on how the view refused: a
 * `ValidationError` raised with a plain string arrives as a list of strings, a
 * field-keyed one as `{field: [messages]}`, `NotFound` and friends as
 * `{detail}`, and our own hand-rolled 400s as `{message}`. Reaching for only
 * one of those - which is what call sites did one at a time - shows the
 * fallback for every other kind and throws away the reason the server gave.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof data === 'string' && data) return data;

  const flatten = (value: unknown): string[] => {
    if (typeof value === 'string') return value ? [value] : [];
    if (Array.isArray(value)) return value.flatMap(flatten);
    return [];
  };

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>;
    for (const key of ['message', 'detail', 'non_field_errors']) {
      const found = flatten(record[key]);
      if (found.length) return found.join(' ');
    }
    const anyField = Object.values(record).flatMap(flatten);
    if (anyField.length) return anyField.join(' ');
  }

  const listed = flatten(data);
  return listed.length ? listed.join(' ') : fallback;
}
